import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Schema.org JSON-LD structured data for the law firm.
 *
 * - <OrganizationSchema /> (Home): LegalService + LocalBusiness + WebSite + Organization
 * - <PracticeAreaSchema name="…" description="…" url="…" /> (Rechtsgebiete-Detailseiten)
 * - <BreadcrumbSchema items={[{name, url}, …]} />
 * - <PersonSchema person={...} /> (Team)
 * - <FAQSchema items={[{question, answer}, …]} /> (Selbsttest, Pricing)
 * - <WebPageSchema name="…" description="…" url="…" />
 */
const SITE_URL = 'https://www.kanzlei-partner.com';
const LOGO_URL = `${SITE_URL}/assets/logo.png`;

const LEGAL_SERVICE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': ['LegalService', 'LocalBusiness'],
  '@id': `${SITE_URL}/#kanzlei`,
  name: 'Kanzlei Nabhan Gäbler & Partner',
  legalName: 'Nabhan Gäbler & Partner Rechtsanwalt Betriebswirte, Partnerschaftsgesellschaft',
  alternateName: ['Nabhan Gäbler & Partner', 'Kanzlei Nabhan Gäbler'],
  url: SITE_URL,
  logo: LOGO_URL,
  image: `${SITE_URL}/assets/hero.jpg`,
  description:
    'Bundesweit tätige Anwalts- und Steuerberater-Kanzlei in Bonn. Schwerpunkte: Ausländerrecht, Einbürgerung, Steuerrecht, Steuerberatung, Strafrecht und Gesellschaftsrecht. Persönliche, mehrsprachige und vollständig digitale Betreuung von Privatpersonen und Unternehmen.',
  telephone: '+49 228 30 40 640',
  email: 'bonn@kanzlei-partner.com',
  priceRange: '€€',
  currenciesAccepted: 'EUR',
  paymentAccepted: 'Überweisung, SEPA-Lastschrift, EC-Karte',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Bundeskanzlerplatz 2D',
    postalCode: '53113',
    addressLocality: 'Bonn',
    addressRegion: 'NRW',
    addressCountry: 'DE',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 50.7187702,
    longitude: 7.1147981,
  },
  areaServed: [
    { '@type': 'Country', name: 'Deutschland' },
    { '@type': 'AdministrativeArea', name: 'Nordrhein-Westfalen' },
    { '@type': 'City', name: 'Bonn' },
    { '@type': 'City', name: 'Köln' },
  ],
  serviceArea: { '@type': 'Country', name: 'Deutschland' },
  knowsLanguage: ['de', 'en', 'fr', 'tr', 'ar', 'it'],
  knowsAbout: [
    'Ausländerrecht',
    'Einbürgerung',
    'Aufenthaltsrecht',
    'Steuerrecht',
    'Steuerstrafrecht',
    'Steuerberatung',
    'Buchhaltung',
    'Strafrecht',
    'Wirtschaftsstrafrecht',
    'Gesellschaftsrecht',
    'GmbH-Gründung',
    'Verwaltungsrecht',
    'Familienrecht',
    'Erbrecht',
    'Mietrecht',
    'Verkehrsrecht',
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:30',
      closes: '13:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '14:00',
      closes: '16:30',
    },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '120',
  },
  sameAs: [
    'https://www.youtube.com/@KanzleiNabhanGabler',
  ],
};

const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'Kanzlei Nabhan Gäbler & Partner',
  publisher: { '@id': `${SITE_URL}/#kanzlei` },
  inLanguage: ['de-DE', 'en-US', 'ar', 'tr-TR'],
};

export const OrganizationSchema = () => (
  <Helmet>
    <script type="application/ld+json">{JSON.stringify(LEGAL_SERVICE_JSONLD)}</script>
    <script type="application/ld+json">{JSON.stringify(WEBSITE_JSONLD)}</script>
  </Helmet>
);

export const PracticeAreaSchema = ({ name, description, url }) => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: name,
    provider: { '@id': `${SITE_URL}/#kanzlei` },
    areaServed: { '@type': 'Country', name: 'Deutschland' },
    name,
    description,
    url,
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(json)}</script>
    </Helmet>
  );
};

export const BreadcrumbSchema = ({ items }) => {
  const json = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(json)}</script>
    </Helmet>
  );
};

export const PersonSchema = ({ person }) => {
  // person: { name, role, title, image, suffix }
  const json = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: `${person.name}${person.suffix ? ' ' + person.suffix : ''}`,
    jobTitle: person.title || person.role,
    image: person.image ? `${SITE_URL}${person.image}` : undefined,
    worksFor: { '@id': `${SITE_URL}/#kanzlei` },
    affiliation: { '@id': `${SITE_URL}/#kanzlei` },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(json)}</script>
    </Helmet>
  );
};

export const FAQSchema = ({ items }) => {
  // items: [{ question, answer }]
  const json = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((it) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: it.answer,
      },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(json)}</script>
    </Helmet>
  );
};

export const WebPageSchema = ({ name, description, url, type = 'WebPage' }) => {
  const json = {
    '@context': 'https://schema.org',
    '@type': type,
    name,
    description,
    url,
    isPartOf: { '@id': `${SITE_URL}/#website` },
    publisher: { '@id': `${SITE_URL}/#kanzlei` },
    inLanguage: 'de-DE',
  };
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(json)}</script>
    </Helmet>
  );
};
